import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { StatusPage } from 'components';
import {
  BookValuesContext,
  DRMModifiersContext,
  DRMValuesContext,
  UserContentValuesContext,
  UserSessionValuesContext,
} from 'contexts';
import { useQueryOffline } from 'hooks';
import DRM_QUERY from 'graphql/queries/DRMQuery.graphql';
import { getOfflineSessionPercentageWithAdditionalMarkers } from 'utilities';

export const DRMProvider = ({ children }) => {
  const { book } = useContext(BookValuesContext);
  const userContent = useContext(UserContentValuesContext);
  const { ownedBookPk } = useContext(UserSessionValuesContext);

  const { error, data } = useQueryOffline(DRM_QUERY, {
    variables: {
      ownedBookPk,
      bookPk: book.pk,
    },
  });

  const [copyEnabled, setCopyEnabled] = useState(false);
  const [copyPercentMaximum, setCopyPercentMaximum] = useState();
  const [copyPercentUsed, setCopyPercentUsed] = useState();
  const [copyPercentRemaining, setCopyPercentRemaining] = useState();

  const [printEnabled, setPrintEnabled] = useState(false);
  const [printPercentMaximum, setPrintPercentMaximum] = useState();
  const [printPercentUsed, setPrintPercentUsed] = useState();
  const [printPercentRemaining, setPrintPercentRemaining] = useState();

  const [offlineEnabled, setOfflineEnabled] = useState(false);
  const [offlinePercentMaximum, setOfflinePercentMaximum] = useState();
  const [offlinePercentUsed, setOfflinePercentUsed] = useState();
  const [offlinePercentRemaining, setOfflinePercentRemaining] = useState();
  const [offlineCountMaximum, setOfflineCountMaximum] = useState();
  const [offlineCountUsed, setOfflineCountUsed] = useState();
  const [offlineCountRemaining, setOfflineCountRemaining] = useState();

  const [
    didAttemptToExceedOfflinePercent,
    setDidAttemptToExceedOfflinePercent,
  ] = useState(false);

  const [notesEnabled, setNotesEnabled] = useState(false);
  const [flashcardsEnabled, setFlashcardsEnabled] = useState();
  const [collaborationEnabled, setCollaborationEnabled] = useState();

  /**
   * @method allowedToCopyAdditionalCharacters
   * @param {Number} additionalCharacterCount - additional number of characters the user is attempting to copy
   * @returns {Boolean} whether the user can copy the specified number of additional characters
   */
  const allowedToCopyAdditionalCharacters = useCallback(
    (additionalCharacterCount = 0) => {
      // No restriction
      if (copyPercentMaximum >= 1) {
        return true;
      }

      const percentAfterIncludingCharacters =
        book.getPercentageOfBookForCharacterCount(
          userContent.copiedCharacterCount + additionalCharacterCount,
        );

      return percentAfterIncludingCharacters <= copyPercentMaximum;
    },
    [book, userContent, copyPercentMaximum],
  );

  /**
   * @method allowedToOfflineAdditionalMarkers
   * @param {Array<BookMarker>} markers - list of BookMarker objects
   * @returns {Boolean} whether the user can take these additional markers offline
   */
  const allowedToOfflineAdditionalMarkers = useCallback(
    (markers) =>
      getOfflineSessionPercentageWithAdditionalMarkers(
        userContent?.offlineSession,
        markers,
      ) <= offlinePercentMaximum,
    [userContent, offlinePercentMaximum],
  );

  useEffect(() => {
    // This data is attached to the epub model on the backend, but probably shouldn't be.
    // It really has more to do with our reader than with the content.
    const drmConfigurationData = data?.drmForEpub;
    if (!drmConfigurationData || !userContent) {
      return;
    }

    // Copy
    const copyEnabled = drmConfigurationData.isCopiable;
    setCopyEnabled(copyEnabled);

    const copyPercentMaximum = Boolean(
      drmConfigurationData.maxCharsCopiable && book.characterCount,
    )
      ? parseFloat(drmConfigurationData.maxCharsCopiable / book.characterCount)
      : 0;
    setCopyPercentMaximum(copyPercentMaximum);

    const copyPercentUsed = book.characterCount
      ? Math.min(
          1, // never greater than 100%
          userContent.copiedCharacterCount / book.characterCount,
        )
      : 0;
    setCopyPercentUsed(copyPercentUsed);

    const copyPercentRemaining = Math.max(
      0, // never less than 0%
      copyPercentMaximum - copyPercentUsed,
    );
    setCopyPercentRemaining(copyPercentRemaining);

    // Print
    const printEnabled = drmConfigurationData.isPrintable;
    setPrintEnabled(printEnabled);

    const printPercentMaximum =
      parseFloat(drmConfigurationData.printAllowancePercent) ?? 0;
    setPrintPercentMaximum(printPercentMaximum);

    // const printPercentUsed = Math.min(
    //   1,
    //   userContent.SOME_PRINT_VALUE_REPLACE_ME / book.characterCount,
    // )
    const printPercentUsed = 0; // Will need a real value derived from user's PrintJob records
    setPrintPercentUsed(printPercentUsed);

    const printPercentRemaining = Math.max(
      0,
      printPercentMaximum - printPercentUsed,
    );
    setPrintPercentRemaining(printPercentRemaining);

    // Offline
    setOfflineEnabled(drmConfigurationData.offlineEnabled);

    const offlinePercentMaximum =
      parseFloat(drmConfigurationData.offlinePercent) ?? 0;
    setOfflinePercentMaximum(offlinePercentMaximum);

    const offlinePercentUsed = userContent.offlineSession?.percentage ?? 0;
    setOfflinePercentUsed(offlinePercentUsed);

    setOfflinePercentRemaining(
      Math.max(0, offlinePercentMaximum - offlinePercentUsed),
    );

    const offlineCountMaximum = drmConfigurationData.offlineSessionLimit ?? 0;
    setOfflineCountMaximum(offlineCountMaximum);

    const offlineCountUsed = userContent.offlineSessions.length;
    setOfflineCountUsed(offlineCountUsed);

    setOfflineCountRemaining(
      Math.max(0, offlineCountMaximum - offlineCountUsed),
    );

    // Misc
    const notesEnabled = !book.notesDisabled;
    setNotesEnabled(notesEnabled);

    const flashcardsEnabled = !book.flashcardsDisabled;
    setFlashcardsEnabled(flashcardsEnabled);

    const collaborationEnabled = !book.collaborationDisabled;
    setCollaborationEnabled(collaborationEnabled);
  }, [book, data, userContent]);

  const values = useMemo(
    () => ({
      allowedToCopyAdditionalCharacters,
      allowedToOfflineAdditionalMarkers,

      didAttemptToExceedOfflinePercent,

      copyEnabled,
      copyPercentMaximum,
      copyPercentUsed,
      copyPercentRemaining,

      printEnabled,
      printPercentMaximum,
      printPercentUsed,
      printPercentRemaining,

      offlineEnabled,
      offlinePercentMaximum,
      offlinePercentUsed,
      offlinePercentRemaining,
      offlineCountMaximum,
      offlineCountUsed,
      offlineCountRemaining,

      notesEnabled,
      flashcardsEnabled,
      collaborationEnabled,
    }),
    [
      allowedToCopyAdditionalCharacters,
      allowedToOfflineAdditionalMarkers,

      didAttemptToExceedOfflinePercent,

      copyEnabled,
      copyPercentMaximum,
      copyPercentUsed,
      copyPercentRemaining,

      printEnabled,
      printPercentMaximum,
      printPercentUsed,
      printPercentRemaining,

      offlineEnabled,
      offlinePercentMaximum,
      offlinePercentUsed,
      offlinePercentRemaining,
      offlineCountMaximum,
      offlineCountUsed,
      offlineCountRemaining,

      notesEnabled,
      flashcardsEnabled,
      collaborationEnabled,
    ],
  );

  const modifiers = useMemo(
    () => ({ setDidAttemptToExceedOfflinePercent }),
    [setDidAttemptToExceedOfflinePercent],
  );

  // Devtools visibility
  window.v7.drm = {
    ...values,
    ...modifiers,
  };

  if (error) {
    return (
      <StatusPage
        statusCode={error.networkError.statusCode}
        bookPk={book?.pk}
      />
    );
  }

  return (
    <DRMValuesContext.Provider value={values}>
      <DRMModifiersContext.Provider value={modifiers}>
        {children}
      </DRMModifiersContext.Provider>
    </DRMValuesContext.Provider>
  );
};

export default DRMProvider;
